import { Controller } from "@hotwired/stimulus"
import { createReadonlyDesigner, applyStepStates } from "../lib/workflow_designer"

// Draws a workflow definition read-only (Review, the channel drawer, the report page) and colours
// its steps by run state. The element needs an explicit height.
//
//   <div data-controller="workflow-drawing"
//        data-workflow-drawing-definition-value='{"steps":{...}}'
//        data-workflow-drawing-states-value='{"summary":"done"}'
//        class="workflow-drawing h-64"></div>
export default class extends Controller {
  static values = { definition: Object, states: Object }

  connect() {
    // A theme change has to redraw even if a build is already running, because the palette is read
    // when the components are created. Coalescing it into the in-flight build the way the other
    // two entry points do would leave a drawing in the old palette until the next resize.
    this.onTheme = () => this.rebuild()
    document.addEventListener("theme:changed", this.onTheme)
    // Only a ready designer can reset its viewport: the library builds its root component in a
    // timeout after create, and a resize that lands before then would throw.
    this.observer = new ResizeObserver(() => {
      if (this.resizeFrame) return
      // A drawing that could not be built on connect is built the moment it gets a box.
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null
        this.refresh()
      })
    })
    this.observer.observe(this.element)
    this.build()
  }

  // The library measures each step's text with getBBox when it builds the component and bakes the
  // width into the rect; nothing remeasures it afterwards. In a display:none ancestor getBBox
  // returns zeros, so every step falls back to the minimum width and stays wrong after it is
  // shown, and some browsers throw instead. So: no box, no build (SMA-106).
  get hasBox() {
    return this.element.offsetWidth > 0 && this.element.offsetHeight > 0
  }

  // Build or re-fit, whichever the element needs. Both entry points come through here: a
  // disclosure revealing the element, and the ResizeObserver. Idempotent.
  refresh() {
    if (!this.hasBox) return
    if (!this.designer) this.build()
    else if (this.ready) this.designer.resetViewport()
  }

  disconnect() {
    document.removeEventListener("theme:changed", this.onTheme)
    this.observer?.disconnect()
    this.destroy()
  }

  statesValueChanged() {
    if (this.designer) applyStepStates(this.element, this.statesValue)
  }

  // `this.designer` is only assigned after two awaits (the library is a lazy chunk), so without a
  // guard the two entry points can both start a build on the same first open: revealing the
  // element gives it a box, which is what refresh() tests and what the ResizeObserver reports.
  // The second build would wipe the first one's DOM and orphan its listeners, and would pay for
  // the per-step getBBox measurement twice on the slowest open.
  build() {
    if (this.buildPromise) return this.buildPromise

    return this.rebuild()
  }

  // Unconditionally, for a caller whose reason to redraw outlives a build already in flight.
  rebuild() {
    this.buildPromise = Promise.resolve(this.buildPromise).then(() => this.create())
    const started = this.buildPromise
    started.finally(() => { if (this.buildPromise === started) this.buildPromise = null })
    return started
  }

  async create() {
    this.destroy()
    if (!this.element.isConnected || !this.hasBox) return
    try {
      this.designer = await createReadonlyDesigner(this.element, this.definitionValue)
      this.designer.onReady.subscribe(() => {
        this.ready = true
        applyStepStates(this.element, this.statesValue)
      })
    } catch (error) {
      this.element.textContent = error.message
    }
  }

  destroy() {
    this.ready = false
    if (this.designer) {
      this.designer.destroy()
      this.designer = null
    }
  }
}
